import * as _ from 'lodash';
import * as React from 'react';
import { mixins } from 'consumer-components';
import { connect } from 'react-redux';
import { DataShape, FormProps, reduxForm } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import { hashHistory } from 'react-router';

import store from 'login-store';
import { setOnboardingFormError } from 'login-actions/onboardingActions';
import * as deploymentConfig from 'login-utils/deploymentConfig';
import * as featureFlags from 'login-utils/featureFlags';
import * as mobileUtils from 'login-utils/mobileUtils';
import * as onboardingRoutingUtils from 'login-utils/onboardingRoutingUtils';
import * as milestonesUtils from './milestonesUtils';
import * as anonymousBorrowersAPI from 'login-apis/anonymousBorrowersAPI';
import * as shortFormAPI from 'login-apis/shortFormAPI';
import * as onboardingForm from 'login-utils/onboardingForm';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import BulletedItem, { BulletedItemBase } from 'login-components/Common/PaneLayout/BulletedItem/BulletedItem';
import ContextLink from 'login-components/Common/ContextLink';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import States from 'login-constants/states';
import styles from 'login-components/Common/PaneLayout/BulletedItem/bulletedItemStyles';
import { setHref } from 'login-utils/globalWrappers';
import { StickyButton } from 'login-components/Common/StickyButton/StickyButton';
import { horizontalPadding, smallViewTopPadding } from 'login-components/Common/PaneLayout/paneStyles';

const buttonStyle = StyleSheet.create({
  customStickyButtonStyle: {
    ...mixins.padding(0, horizontalPadding, smallViewTopPadding),
  },
});

export interface MilestonesProps {
  loanType: LoanType;
  viewType: string;
  pageType: milestonesUtils.MilestonesPageType;
  milestones: BulletedItemBase[];
  error: string;
}

export interface MilestoneState {
  buttonDisabled: boolean;
}

export class Milestones extends React.Component<
  MilestonesProps & FormProps<DataShape, {}, ReduxState>,
  MilestoneState
> {
  private shortFormRef = React.createRef<HTMLFormElement>();

  constructor(props) {
    super(props);
    this.state = {
      buttonDisabled: false,
    };
  }

  async onSubmitMilestones(values, loanType) {
    try {
      this.setState({
        buttonDisabled: true,
      });

      const res = onboardingRoutingUtils.isShortFormEligible(values.propertySearchType)
        ? await shortFormAPI.shortFormSubmit(values, loanType)
        : await anonymousBorrowersAPI.milestonesSubmit(values, loanType);
      setHref(res.data.redirectUrl ? res.data.redirectUrl : '/');
    } catch (error) {
      store.dispatch(setOnboardingFormError(_.get(error, 'response.data', 'There was an error.')));
      // Usually, errors are caused by bad data entered into the short form, so we send the user back to correct them
      if (onboardingRoutingUtils.isShortFormEligible(values.propertySearchType)) {
        return hashHistory.push(States.SHORT_FORM);
      }
      // If we can't go back to short form, we un-disable the button and show the error on this page instead
      return this.setState({
        buttonDisabled: false,
      });
    }
  }

  renderButton() {
    const { handleSubmit, loanType, pageType } = this.props;
    const { buttonDisabled } = this.state;
    const hideButton = pageType === 'UNSUPPORTED_LOAN_TYPE';
    const allowDelayedAccountSubmissions =
      // delayed account creation config on
      deploymentConfig.isDelayedAccountCreation() &&
      // only MORTGAGE loan type is allowed OR the selected loan type is MORTGAGE
      (_.isEqual(deploymentConfig.get().supportedLoanTypes, ['MORTGAGE']) || loanType === 'MORTGAGE');

    const allowNewToBankSubmissions =
      deploymentConfig.isNewToBank() && featureFlags.isEnabled('truncatedEConsentForNTB20190522');

    if (hideButton) {
      return;
    } else if (allowDelayedAccountSubmissions || allowNewToBankSubmissions) {
      return (
        <form
          ref={this.shortFormRef}
          className={css(styles.buttonWrapper)}
          onSubmit={handleSubmit!(values => this.onSubmitMilestones(values, loanType))}
        >
          <PrimaryButton
            text="Continue"
            fontFamily="Roboto"
            data-it="Milestones-delayedAccountCreation"
            buttonId="it-milestonesSubmit"
            disabled={buttonDisabled}
          />
        </form>
      );
    } else {
      return (
        <div className={css(styles.buttonWrapper)}>
          <ContextLink className={css(styles.contextLink)} to={States.CREATE_ACCOUNT}>
            <PrimaryButton text="Continue" fontFamily="Roboto" data-it="Milestones-createAccount" />
          </ContextLink>
        </div>
      );
    }
  }

  render() {
    const { milestones, pageType, viewType, error } = this.props;

    return (
      <div className="it-Milestones">
        <div className={css(styles.wrapper)}>
          <div className={css(styles.bulletWrapper)}>
            {pageType === 'POST_SHORT_FORM' ? (
              <div
                className={css(styles.introDetailsWrapper, styles.stepWrapperPaddingLeft)}
                data-it="Milestones-startingDetails"
              >
                <div className={css(styles.titleWrapper, styles.stepWrapperPaddingBottom)}>
                  <h2 className={css(styles.title, styles.notFirstTitle, styles.introDetails)}>
                    Provide starting details
                  </h2>
                </div>
                <h2 className={css(styles.heading)}>Next</h2>
              </div>
            ) : (
              <div className={css(styles.stepWrapperPaddingLeft)}>
                <h2 className={css(styles.heading)}>Next</h2>
              </div>
            )}
            <div>{milestones.map((config, i) => <BulletedItem key={i} index={i} {...config} />)}</div>
          </div>
          {error && (
            <div className={css(styles.errorWrapper)} data-it="Milestones-errorMessage">
              <ErrorMessage errorText={error} />
            </div>
          )}
          {viewType === mobileUtils.viewTypes.MOBILE ? (
            <StickyButton
              customBottomPadding={smallViewTopPadding}
              customStickyButtonStyle={buttonStyle.customStickyButtonStyle}
              data-it="MilestoneStickyButton"
            >
              {this.renderButton()}
            </StickyButton>
          ) : (
            this.renderButton()
          )}
        </div>
      </div>
    );
  }
}

const ConnectedMilestones = connect((state: ReduxState) => {
  const loanType = onboardingForm.getLoanType(state);
  const timelineValue = onboardingForm.getTimelineValue(state);

  return {
    loanType,
    viewType: state.global.viewType,
    pageType: milestonesUtils.getPageType(loanType, timelineValue),
    milestones: milestonesUtils.getMilestonesRightPaneCopy(loanType, timelineValue),
    error: _.get(state, 'onboarding.onboardingFormError') as string,
  };
})(Milestones);

const FormConnectedMilestones = reduxForm<FormProps<DataShape, {}, ReduxState>, {}, ReduxState>({
  ...onboardingForm.FORM_CONFIG,
})(ConnectedMilestones);

export default FormConnectedMilestones;
